// TabManager: Manages tab state and persistence
import { Tab, PersistedTab } from './types';
import { PersistenceManager } from './PersistenceManager';
import { uuid } from '../utils/uuid';

export class TabManager {
  tabs: Tab[] = [];
  activeTabId: string | null = null;
  private persistenceManager: PersistenceManager;
  private onChangeCallback?: () => void;

  constructor(persistenceManager: PersistenceManager) {
    this.persistenceManager = persistenceManager;
  }

  onChange(callback: () => void): void {
    this.onChangeCallback = callback;
  }

  private notifyChange(): void {
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }

  createTab(fileName: string, content: string, language: string, fileHandle?: FileSystemFileHandle): Tab {
    const tab: Tab = {
      id: uuid(),
      fileName,
      content,
      language,
      unsaved: false,
      pinned: false,
      fileHandle,
      lastModified: Date.now(),
    };
    this.tabs.push(tab);
    this.activeTabId = tab.id;
    this.saveTabs();
    this.notifyChange();
    return tab;
  }

  closeTab(tabId: string): boolean {
    const index = this.tabs.findIndex((t) => t.id === tabId);
    if (index === -1) return false;

    const tab = this.tabs[index];
    
    // TODO: Add confirmation dialog if unsaved
    if (tab.unsaved) {
      const confirmed = confirm(`"${tab.fileName}" has unsaved changes. Close anyway?`);
      if (!confirmed) return false;
    }

    this.tabs.splice(index, 1);
    this.persistenceManager.clearTab(tabId);

    // Switch to adjacent tab or clear active
    if (this.activeTabId === tabId) {
      if (this.tabs.length > 0) {
        const newIndex = Math.min(index, this.tabs.length - 1);
        this.activeTabId = this.tabs[newIndex].id;
      } else {
        this.activeTabId = null;
      }
    }

    this.saveTabs();
    this.notifyChange();
    return true;
  }

  switchTab(tabId: string): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (tab) {
      this.activeTabId = tabId;
      this.persistenceManager.saveAppState('activeTabId', tabId);
      this.notifyChange();
    }
  }

  markUnsaved(tabId: string): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (tab) {
      tab.unsaved = true;
      this.notifyChange();
    }
  }

  markSaved(tabId: string): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (tab) {
      tab.unsaved = false;
      this.notifyChange();
    }
  }

  updateTabContent(tabId: string, content: string): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (tab) {
      tab.content = content;
      tab.lastModified = Date.now();
      tab.unsaved = true;
      this.autoSaveTab(tab);
      this.notifyChange();
    }
  }

  pinTab(tabId: string): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (tab) {
      tab.pinned = true;
      // Move pinned tabs to front
      this.tabs = [...this.tabs.filter((t) => t.pinned), ...this.tabs.filter((t) => !t.pinned)];
      this.saveTabs();
      this.notifyChange();
    }
  }

  unpinTab(tabId: string): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (tab) {
      tab.pinned = false;
      this.saveTabs();
      this.notifyChange();
    }
  }

  getActiveTab(): Tab | null {
    return this.tabs.find((t) => t.id === this.activeTabId) || null;
  }

  getTab(tabId: string): Tab | null {
    return this.tabs.find((t) => t.id === tabId) || null;
  }

  private autoSaveTab(tab: Tab): void {
    const persisted: PersistedTab = {
      id: tab.id,
      fileName: tab.fileName,
      content: tab.content,
      language: tab.language,
      unsaved: tab.unsaved,
      pinned: tab.pinned,
      lastModified: tab.lastModified,
    };
    this.persistenceManager.saveTab(persisted);
  }

  async saveTabs(): Promise<void> {
    const persisted: PersistedTab[] = this.tabs.map((tab) => ({
      id: tab.id,
      fileName: tab.fileName,
      content: tab.content,
      language: tab.language,
      unsaved: tab.unsaved,
      pinned: tab.pinned,
      lastModified: tab.lastModified,
    }));
    await this.persistenceManager.saveTabs(persisted);
  }

  async restoreTabs(): Promise<void> {
    const persisted = await this.persistenceManager.loadTabs();
    this.tabs = persisted.map((p) => ({
      id: p.id,
      fileName: p.fileName,
      content: p.content,
      language: p.language,
      unsaved: p.unsaved,
      pinned: p.pinned,
      fileHandle: undefined, // Not persisted for security
      lastModified: p.lastModified,
    }));

    const savedActiveId = await this.persistenceManager.loadAppState('activeTabId');
    if (savedActiveId && this.tabs.find((t) => t.id === savedActiveId)) {
      this.activeTabId = savedActiveId;
    } else if (this.tabs.length > 0) {
      this.activeTabId = this.tabs[0].id;
    }

    this.notifyChange();
  }
}
